import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { io } from 'socket.io-client';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { QrReader } from 'react-qr-reader';
import { useLang } from '../LanguageContext';
import { useAuth } from '../context/AuthContext';
import { useFarm } from '../context/FarmContext';
import { isFarmLeadRole, isGlobalRole } from '../utils/roles';
import { getFarmOverviewPath } from '../utils/farmRoutes';
import { formatRoomFormApiError } from '../utils/roomFormApiError';
import { authFetch } from '../utils/authFetch';
import { normalizeControllerType } from '../utils/controllerTypes';
import { emitJoinFarmRooms, subscribeSocketFarmRooms } from '../utils/farmSocketRooms';
import { isRateLimitBackoffActive } from '../utils/rateLimitBackoff';
import { getSocketIoServerUrl, SOCKET_IO_CLIENT_OPTIONS } from '../utils/socketIoClientOptions';
import { isDeviceOnline, formatDeviceTimestamp } from '../utils/timeUtils';
import { mapApiActiveDeviceToCardDevice } from '../utils/mapApiActiveDeviceToCardDevice';
import DeviceCard from '../components/DeviceCard';
import WaterStatusCard from '../components/WaterStatusCard';
import ShefaPageLoadOverlay from '../components/ui/ShefaPageLoadOverlay';
import RoomFormModal from '../components/RoomFormModal';
import './RoomPage.css';

const OFFLINE_THRESHOLD_MS = 5 * 60 * 1000;
let sharedRoomDeviceSocket = null;

function getSharedRoomDeviceSocket() {
  if (!sharedRoomDeviceSocket) {
    sharedRoomDeviceSocket = io(getSocketIoServerUrl(), SOCKET_IO_CLIENT_OPTIONS);
    subscribeSocketFarmRooms(sharedRoomDeviceSocket);
  }
  return sharedRoomDeviceSocket;
}

function firstDefined(...values) {
  return values.find((value) => value !== undefined && value !== null && value !== '');
}

function asPlainObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

function getLivePayloadDeviceId(payload = {}) {
  return String(payload?.deviceId || payload?.device_id || payload?.id || '').trim();
}

function particleDeviceIdsEqual(a, b) {
  const s = String(a || '').trim();
  const t = String(b || '').trim();
  if (!s || !t) return false;
  if (s === t) return true;
  if (/^[a-f0-9]{24}$/i.test(s) && /^[a-f0-9]{24}$/i.test(t)) return s.toLowerCase() === t.toLowerCase();
  return false;
}

function telemetryTimeMs(source = {}) {
  const ts =
    source.lastRealSensorUpdateAt ||
    source.latestTelemetryAt ||
    source.lastTelemetryAt ||
    source.latestTelemetry?.timestamp ||
    source.timestamp ||
    source.ts ||
    null;
  const ms = ts ? new Date(ts).getTime() : 0;
  return Number.isFinite(ms) ? ms : 0;
}

function waterRoomSocketDebug(...args) {
  try {
    if (typeof localStorage !== 'undefined' && localStorage.getItem('waterSocketDebug') === '1') {
      console.debug('[water:room-socket]', ...args);
    }
  } catch (_) {
    /* debug only */
  }
}

function waterReadingSnapshot(source = {}) {
  const safeSource = asPlainObject(source);
  const telemetry = asPlainObject(safeSource.latestTelemetry);
  const data = asPlainObject(safeSource.data);
  return {
    ph: firstDefined(telemetry.ph, safeSource.ph, safeSource.pH, safeSource.PH, data.ph, data.pH, data.PH, null),
    ec: firstDefined(telemetry.ec, safeSource.ec, safeSource.EC, data.ec, data.EC, data.ec_mscm, null),
    orp: firstDefined(telemetry.orp, safeSource.orp, safeSource.ORP, data.orp, data.ORP, data.orp_mv, null),
    temp: firstDefined(
      telemetry.temp,
      telemetry.ecTemp,
      telemetry.temperature,
      safeSource.temp,
      safeSource.ecTemp,
      safeSource.temperature,
      data.temp,
      data.ecTemp,
      data.temperature,
      data.temp_c,
      null
    ),
    level: firstDefined(telemetry.level, safeSource.level, safeSource.waterLevel, data.level, data.waterLevel, data.water_level, null),
    levelCm: firstDefined(telemetry.levelCm, telemetry.level_cm, safeSource.levelCm, safeSource.level_cm, data.levelCm, data.level_cm, null),
    volume: firstDefined(telemetry.volume, safeSource.volume, data.volume, null),
    levelVoltage: firstDefined(
      telemetry.levelVoltage,
      telemetry.level_voltage,
      safeSource.levelVoltage,
      safeSource.level_voltage,
      data.levelVoltage,
      data.level_voltage,
      null
    ),
  };
}

function hasWaterReadingValue(source = {}) {
  return Object.values(waterReadingSnapshot(source)).some((value) => value !== undefined && value !== null && value !== '');
}

function isWaterLikeDevice(deviceLike = {}) {
  return normalizeControllerType(
    deviceLike.controllerType,
    deviceLike.controller,
    deviceLike.deviceType,
    deviceLike.type,
    deviceLike.kind
  ) === 'water';
}

function mergeWaterRoomLivePayload(prev = {}, payload = {}, options = {}) {
  const previous = asPlainObject(prev);
  const incoming = asPlainObject(payload);
  const incomingTelemetry = asPlainObject(incoming.latestTelemetry);
  const data = asPlainObject(incoming.data);
  const source = {
    ...data,
    ...incoming,
    ...incomingTelemetry,
  };
  const telemetryTimestamp =
    incoming.lastRealSensorUpdateAt ||
    incoming.latestTelemetryAt ||
    incoming.lastTelemetryAt ||
    incomingTelemetry.timestamp ||
    incoming.timestamp ||
    incoming.ts ||
    (options.statusOnly ? previous.lastRealSensorUpdateAt || previous.latestTelemetryAt || previous.lastTelemetryAt || previous.timestamp : null) ||
    new Date().toISOString();
  const incomingMs = telemetryTimestamp ? new Date(telemetryTimestamp).getTime() : 0;
  const prevMs = telemetryTimeMs(previous);
  if (!options.statusOnly && Number.isFinite(incomingMs) && incomingMs > 0 && prevMs > 0 && incomingMs < prevMs) {
    waterRoomSocketDebug('device_data stale ignored', {
      incomingId: getLivePayloadDeviceId(incoming),
      incoming: telemetryTimestamp,
      previous:
        previous.lastRealSensorUpdateAt ||
        previous.latestTelemetryAt ||
        previous.lastTelemetryAt ||
        previous.latestTelemetry?.timestamp ||
        previous.timestamp,
    });
    return previous;
  }
  const telemetryReason = firstDefined(
    source.telemetry_reason,
    source.telemetryReason,
    previous.latestTelemetry?.telemetry_reason,
    previous.telemetry_reason
  );
  const nextStatus = incoming.deviceStatus?.status || incoming.status || previous.deviceStatus?.status || previous.status || 'online';
  const nextLatestTelemetry = options.statusOnly
    ? asPlainObject(previous.latestTelemetry)
    : {
        ...asPlainObject(previous.latestTelemetry),
        ...incomingTelemetry,
        timestamp: incomingTelemetry.timestamp || telemetryTimestamp,
        ph: firstDefined(source.ph, source.pH, source.PH, previous.latestTelemetry?.ph),
        ec: firstDefined(source.ec, source.EC, source.ec_mscm, previous.latestTelemetry?.ec),
        orp: firstDefined(source.orp, source.ORP, source.orp_mv, previous.latestTelemetry?.orp),
        temp: firstDefined(source.temp, source.ecTemp, source.temperature, source.temp_c, previous.latestTelemetry?.temp),
        temperature: firstDefined(source.temperature, source.temp, source.ecTemp, source.temp_c, previous.latestTelemetry?.temperature),
        ecTemp: firstDefined(source.ecTemp, source.temp, source.temperature, source.temp_c, previous.latestTelemetry?.ecTemp),
        level: firstDefined(source.level, source.waterLevel, source.water_level, previous.latestTelemetry?.level),
        levelCm: firstDefined(source.levelCm, source.level_cm, previous.latestTelemetry?.levelCm),
        volume: firstDefined(source.volume, previous.latestTelemetry?.volume),
        levelVoltage: firstDefined(source.levelVoltage, source.level_voltage, previous.latestTelemetry?.levelVoltage),
        phError: firstDefined(source.phError, previous.latestTelemetry?.phError),
        ecError: firstDefined(source.ecError, previous.latestTelemetry?.ecError),
        orpError: firstDefined(source.orpError, previous.latestTelemetry?.orpError),
        tankMaxHeight: firstDefined(source.tankMaxHeight, previous.latestTelemetry?.tankMaxHeight),
        tankFullVolume: firstDefined(source.tankFullVolume, previous.latestTelemetry?.tankFullVolume),
        transport: firstDefined(source.transport, previous.latestTelemetry?.transport),
        telemetry_reason: telemetryReason,
        telemetryReason: telemetryReason,
      };

  return {
    ...previous,
    ...incoming,
    status: nextStatus,
    timestamp: telemetryTimestamp,
    lastUpdated: telemetryTimestamp,
    lastTelemetryAt: telemetryTimestamp,
    latestTelemetryAt: telemetryTimestamp,
    lastRealSensorUpdateAt: telemetryTimestamp,
    latestTelemetry: nextLatestTelemetry,
    data: {
      ...asPlainObject(previous.data),
      ...data,
    },
    deviceStatus: {
      ...asPlainObject(previous.deviceStatus),
      ...asPlainObject(incoming.deviceStatus),
      status: nextStatus,
      lastSeenAt: incoming.deviceStatus?.lastSeenAt || incoming.timestamp || telemetryTimestamp,
    },
    ph: firstDefined(source.ph, source.pH, source.PH, previous.ph),
    ec: firstDefined(source.ec, source.EC, source.ec_mscm, previous.ec),
    orp: firstDefined(source.orp, source.ORP, source.orp_mv, previous.orp),
    temp: firstDefined(source.temp, source.ecTemp, source.temperature, source.temp_c, previous.temp),
    temperature: firstDefined(source.temperature, source.temp, source.ecTemp, source.temp_c, previous.temperature),
    ecTemp: firstDefined(source.ecTemp, source.temp, source.temperature, source.temp_c, previous.ecTemp),
    level: firstDefined(source.level, source.waterLevel, source.water_level, previous.level),
    levelCm: firstDefined(source.levelCm, source.level_cm, previous.levelCm),
    volume: firstDefined(source.volume, previous.volume),
    levelVoltage: firstDefined(source.levelVoltage, source.level_voltage, previous.levelVoltage),
    phError: firstDefined(source.phError, previous.phError),
    ecError: firstDefined(source.ecError, previous.ecError),
    orpError: firstDefined(source.orpError, previous.orpError),
    tankMaxHeight: firstDefined(source.tankMaxHeight, previous.tankMaxHeight),
    tankFullVolume: firstDefined(source.tankFullVolume, previous.tankFullVolume),
    transport: firstDefined(source.transport, previous.transport),
    telemetry_reason: telemetryReason,
    telemetryReason,
    meta: {
      ...asPlainObject(previous.meta),
      ...asPlainObject(incoming.meta),
      ...(source.transport ? { transport: source.transport } : {}),
    },
  };
}

function mergeRoomApiSnapshotWithLiveWater(prevRoom, nextRoom) {
  const previousDevices = Array.isArray(prevRoom?.devices) ? prevRoom.devices : [];
  const nextDevices = Array.isArray(nextRoom?.devices) ? nextRoom.devices : [];
  if (!previousDevices.length || !nextDevices.length) return nextRoom;

  const mergedDevices = nextDevices.map((nextDevice) => {
    const nextId = nextDevice?.deviceId || nextDevice?.device_id || nextDevice?.id;
    const previous = previousDevices.find((device) =>
      particleDeviceIdsEqual(nextId, device?.deviceId || device?.device_id || device?.id)
    );
    if (!previous || !isWaterLikeDevice({ ...nextDevice, ...previous })) return nextDevice;

    const prevMs = telemetryTimeMs(previous);
    const nextMs = telemetryTimeMs(nextDevice);
    if (hasWaterReadingValue(previous) && prevMs > 0 && (nextMs <= 0 || prevMs >= nextMs)) {
      const merged = mergeWaterRoomLivePayload(nextDevice, previous, { source: 'preserve-live' });
      waterRoomSocketDebug('preserved live water readings over room poll', {
        deviceId: nextId,
        previousTimestamp:
          previous.lastRealSensorUpdateAt ||
          previous.latestTelemetryAt ||
          previous.lastTelemetryAt ||
          previous.latestTelemetry?.timestamp ||
          previous.timestamp ||
          null,
        apiTimestamp:
          nextDevice.lastRealSensorUpdateAt ||
          nextDevice.latestTelemetryAt ||
          nextDevice.lastTelemetryAt ||
          nextDevice.latestTelemetry?.timestamp ||
          nextDevice.timestamp ||
          null,
        readings: waterReadingSnapshot(merged),
      });
      return merged;
    }
    return nextDevice;
  });

  return {
    ...nextRoom,
    devices: mergedDevices,
  };
}

function mergeWaterPayloadIntoRoomDevices(prevRoom, incoming = {}, options = {}) {
  const incomingId = getLivePayloadDeviceId(incoming);
  const devices = Array.isArray(prevRoom?.devices) ? prevRoom.devices : [];
  if (!devices.length) {
    waterRoomSocketDebug('device_data ignored: room has no devices', { incomingId });
    return prevRoom;
  }
  const index = devices.findIndex((device) =>
    particleDeviceIdsEqual(incomingId, device?.deviceId || device?.device_id || device?.id)
  );
  if (index < 0) {
    waterRoomSocketDebug('device_data ignored: device not in room', {
      incomingId,
      knownIds: devices.map((device) => device?.deviceId || device?.device_id || device?.id).filter(Boolean),
    });
    return prevRoom;
  }
  const current = devices[index];
  if (!isWaterLikeDevice({ ...current, ...incoming })) {
    waterRoomSocketDebug('device_data ignored: not water controller', {
      incomingId,
      controller: incoming.controller || incoming.controllerType || current?.controller || current?.controllerType || null,
    });
    return prevRoom;
  }
  const merged = mergeWaterRoomLivePayload(current, incoming, options);
  if (merged === current) return prevRoom;
  const nextDevices = [...devices];
  nextDevices[index] = merged;
  waterRoomSocketDebug(options.statusOnly ? 'device_status accepted' : 'device_data accepted', {
    incomingId,
    roomId: options.roomId || null,
    source: options.source || null,
    timestamp:
      incoming.lastRealSensorUpdateAt ||
      incoming.latestTelemetryAt ||
      incoming.lastTelemetryAt ||
      incoming.latestTelemetry?.timestamp ||
      incoming.timestamp ||
      incoming.ts ||
      null,
    incomingReadings: waterReadingSnapshot(incoming),
    mergedReadings: waterReadingSnapshot(merged),
  });
  return {
    ...prevRoom,
    devices: nextDevices,
  };
}

function extractDeviceId(rawValue) {
  const raw = String(rawValue || '').trim();
  if (!raw) return '';

  try {
    const asUrl = new URL(raw);
    const fromQuery = (asUrl.searchParams.get('deviceId') || asUrl.searchParams.get('id') || '').trim();
    if (fromQuery) return fromQuery;
  } catch (_) {
    // not a URL, continue
  }

  try {
    const parsed = JSON.parse(raw);
    const fromJson = (parsed?.deviceId || parsed?.id || '').trim();
    if (fromJson) return fromJson;
  } catch (_) {
    // not JSON, continue
  }

  const firstToken = raw.split(/[\s,;|]+/).find(Boolean) || '';
  return firstToken.trim();
}

function average(nums) {
  const list = nums.filter((n) => typeof n === 'number' && !Number.isNaN(n));
  if (!list.length) return null;
  return list.reduce((a, b) => a + b, 0) / list.length;
}

function hasMetricValue(value) {
  return typeof value !== 'undefined' && value !== null && Number.isFinite(Number(value));
}

const SEVERE = 2;
const WARN = 1;

function mergeSev(a, b) {
  if (a === SEVERE || b === SEVERE) return SEVERE;
  if (a === WARN || b === WARN) return WARN;
  return 0;
}

/** @param {[number, number]|null|undefined} range */
function severityForRange(minV, maxV, range) {
  if (!range || range.length < 2) return 0;
  const lo = Math.min(range[0], range[1]);
  const hi = Math.max(range[0], range[1]);
  const span = Math.max(hi - lo, 1e-6);
  const margin = span * 0.15;
  let s = 0;
  const check = (v) => {
    if (v == null || !Number.isFinite(v)) return;
    if (v < lo || v > hi) {
      const dist = v < lo ? lo - v : v - hi;
      s = mergeSev(s, dist > margin ? SEVERE : WARN);
    }
  };
  check(minV);
  check(maxV);
  return s;
}

function severityForValue(v, range) {
  return severityForRange(v, v, range);
}

function sevClass(s) {
  if (s === SEVERE) return 'shefa-metric-severe';
  if (s === WARN) return 'shefa-metric-warn';
  return '';
}

function fmtMinMax(minV, maxV, digits, suffix) {
  if (minV == null && maxV == null) return '—';
  const f = (x) => Number(x).toFixed(digits);
  if (minV != null && maxV != null) return `${f(minV)}–${f(maxV)}${suffix}`;
  const v = minV ?? maxV;
  return `${f(v)}${suffix}`;
}

export default function RoomPage() {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const { token, logout, ready, user } = useAuth();
  const { farmName, effectiveFarmId } = useFarm();
  const [room, setRoom] = useState(null);
  const [logs, setLogs] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [scannerEnabled, setScannerEnabled] = useState(false);
  const [manualDeviceId, setManualDeviceId] = useState('');
  const [claimDeviceId, setClaimDeviceId] = useState('');
  const [claimLoading, setClaimLoading] = useState(false);
  const [claimError, setClaimError] = useState('');
  const [claimSuccess, setClaimSuccess] = useState('');
  const [dailySummary, setDailySummary] = useState(null);
  const [dailySummaryLoading, setDailySummaryLoading] = useState(false);
  const [dailySummaryErr, setDailySummaryErr] = useState('');
  const [dayNotes, setDayNotes] = useState({});
  const [savingDayKey, setSavingDayKey] = useState(null);
  const [roomFormOpen, setRoomFormOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [cycleForm, setCycleForm] = useState({
    dayCycleEnabled: false,
    dayStartTime: '06:00',
    dayEndTime: '00:00',
  });
  const [cycleSaving, setCycleSaving] = useState(false);
  const [cycleMessage, setCycleMessage] = useState('');
  const dir = lang === 'he' ? 'rtl' : 'ltr';

  const canManageRoom = isGlobalRole(user?.role) || isFarmLeadRole(user?.role);

  const farmOverviewPath = useMemo(
    () => getFarmOverviewPath(String(room?.farmId || effectiveFarmId || '')) || '/',
    [room?.farmId, effectiveFarmId]
  );

  const handleUnauthorized = useCallback(() => {
    logout();
  }, [logout]);

  const load = useCallback(
    async (opts = {}) => {
      const silent = Boolean(opts.silent);
      if (!token || !roomId) return;
      if (!silent) {
        setLoading(true);
      }
      setError('');
      try {
        const resRoom = await authFetch(
          `/api/rooms/${encodeURIComponent(roomId)}`,
          {},
          { token, onUnauthorized: handleUnauthorized }
        );
        if (!resRoom.ok) {
          if (!silent) {
            setError(t('roomsPage.loadError'));
            setRoom(null);
            setLogs([]);
          }
          return;
        }
        const roomData = await resRoom.json();
        setRoom((prevRoom) => mergeRoomApiSnapshotWithLiveWater(prevRoom, roomData));
        const resLogs = await authFetch(
          `/api/rooms/${encodeURIComponent(roomId)}/growing-logs`,
          {},
          {
            token,
            onUnauthorized: handleUnauthorized,
            farmContextId: roomData?.farmId ? String(roomData.farmId) : undefined,
          }
        );
        const logData = resLogs.ok ? await resLogs.json() : [];
        setLogs(Array.isArray(logData) ? logData : []);
      } catch {
        if (!silent) {
          setError(t('roomsPage.loadError'));
        }
      } finally {
        if (!silent) {
          setLoading(false);
        }
      }
    },
    [token, roomId, handleUnauthorized, t]
  );

  const saveRoomFromModal = useCallback(
    async (body) => {
      const res = await authFetch(
        `/api/rooms/${encodeURIComponent(roomId)}`,
        {
          method: 'PATCH',
          body: JSON.stringify(body),
          headers: { 'Content-Type': 'application/json' },
        },
        {
          token,
          onUnauthorized: handleUnauthorized,
          farmContextId: room?.farmId ? String(room.farmId) : undefined,
        }
      );
      if (!res.ok) {
        const msg = await formatRoomFormApiError(res, t);
        throw new Error(msg);
      }
      await load({ silent: true });
    },
    [roomId, token, room?.farmId, handleUnauthorized, load, t]
  );

  const deleteRoom = useCallback(async () => {
    if (!room?.farmId) return;
    if (!window.confirm(t('dashboard.rooms.confirmDeleteRoom'))) return;
    setDeleting(true);
    try {
      const res = await authFetch(
        `/api/rooms/${encodeURIComponent(roomId)}`,
        { method: 'DELETE' },
        {
          token,
          onUnauthorized: handleUnauthorized,
          farmContextId: String(room.farmId),
        }
      );
      if (!res.ok) {
        setError(t('roomsPage.loadError'));
        return;
      }
      const back = getFarmOverviewPath(String(room.farmId));
      navigate(back || '/');
    } finally {
      setDeleting(false);
    }
  }, [room, roomId, token, t, handleUnauthorized, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!room) return;
    setCycleForm({
      dayCycleEnabled: room.dayCycleEnabled === true || room.dayNightCycle?.enabled === true,
      dayStartTime: room.dayStartTime || room.dayNightCycle?.dayStartTime || '06:00',
      dayEndTime: room.dayEndTime || room.dayNightCycle?.dayEndTime || '00:00',
    });
    setCycleMessage('');
  }, [room]);

  /** When WebSocket fails, still refresh room device readings from the API. */
  useEffect(() => {
    if (!roomId || !token) return undefined;
    const intervalMs = Number(process.env.REACT_APP_ROOM_POLL_MS) || 60_000;
    const id = window.setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      if (isRateLimitBackoffActive()) return;
      load({ silent: true });
    }, intervalMs);
    return () => window.clearInterval(id);
  }, [roomId, token, load]);

  useEffect(() => {
    if (!roomId || !token) return undefined;
    const socket = getSharedRoomDeviceSocket();
    emitJoinFarmRooms(socket);

    const updateRoomDevice = (payload = {}, options = {}) => {
      const incoming = asPlainObject(payload);
      const incomingId = getLivePayloadDeviceId(incoming);
      if (!incomingId) {
        waterRoomSocketDebug('device_data ignored: missing device id', { payload: incoming });
        return;
      }
      const incomingRoomId = firstDefined(incoming.roomId, incoming.room_id);
      if (incomingRoomId && String(incomingRoomId) !== String(roomId)) {
        waterRoomSocketDebug('device_data ignored: different room', {
          incomingId,
          incomingRoomId,
          roomId,
        });
        return;
      }

      setRoom((prevRoom) => mergeWaterPayloadIntoRoomDevices(prevRoom, incoming, { ...options, roomId }));
    };

    const handleDeviceData = (payload = {}) => updateRoomDevice(payload, { source: 'socket' });
    const handleDeviceStatus = (payload = {}) => updateRoomDevice(payload, { source: 'socket-status', statusOnly: true });

    socket.on('device_data', handleDeviceData);
    socket.on('device_status', handleDeviceStatus);
    return () => {
      socket.off('device_data', handleDeviceData);
      socket.off('device_status', handleDeviceStatus);
    };
  }, [roomId, token]);

  const checkOnlineStatus = useCallback((deviceLike = {}) => {
    const mqttEnabled =
      deviceLike.mqttEnabled === true || deviceLike.meta?.mqttEnabled === true;
    if (mqttEnabled) {
      return String(deviceLike.status || '').trim().toLowerCase() !== 'offline';
    }
    const ts = deviceLike.timestamp || deviceLike.lastUpdated || deviceLike.lastSeenAtIso;
    if (!ts) return false;
    const asDate = new Date(ts);
    if (!Number.isFinite(asDate.getTime())) {
      return isDeviceOnline(ts);
    }
    return Date.now() - asDate.getTime() <= OFFLINE_THRESHOLD_MS;
  }, []);

  const cardDevices = useMemo(() => {
    const raw = Array.isArray(room?.devices) ? room.devices : [];
    return raw.map((d) => mapApiActiveDeviceToCardDevice(d, checkOnlineStatus));
  }, [room?.devices, checkOnlineStatus]);

  useEffect(() => {
    const validation = room?.dayNightValidation;
    if (!validation || typeof console === 'undefined' || typeof console.debug !== 'function') return;
    console.debug('[UI][FreshnessSource][RoomDayNightValidation]', {
      roomId,
      source: 'room.dayNightValidation.lastCheckedAt (latest climate_data sample timestamp)',
      lastCheckedAt: validation.lastCheckedAt || null,
      status: validation.status || null,
      reason: validation.reason || null,
      deviceId: validation.deviceId || null,
    });
  }, [
    roomId,
    room?.dayNightValidation?.lastCheckedAt,
    room?.dayNightValidation?.status,
    room?.dayNightValidation?.reason,
    room?.dayNightValidation?.deviceId,
  ]);

  const kpis = useMemo(() => {
    const climate = cardDevices.filter((d) => d.controller !== 'water');
    const water = cardDevices.filter((d) => d.controller === 'water');
    const temps = climate.map((d) => d.temperature);
    const rhs = climate.map((d) => d.humidity);
    const co2s = climate.map((d) => d.co2);
    const phs = water.map((d) => d.ph);
    const ecs = water.map((d) => d.ec);
    const levels = water.map((d) => d.level);
    const volumes = water.map((d) => d.volume);
    const online = cardDevices.filter((d) => d.status === 'online').length;
    return {
      temp: average(temps),
      rh: average(rhs),
      co2: average(co2s),
      ph: average(phs),
      ec: average(ecs),
      level: average(levels),
      volume: average(volumes),
      hasClimate: climate.length > 0,
      hasWater: water.length > 0,
      online,
      total: cardDevices.length,
    };
  }, [cardDevices]);

  const hasDevices = cardDevices.length > 0;
  const kpiLabelPh = t('roomsPage.kpiPh') !== 'roomsPage.kpiPh' ? t('roomsPage.kpiPh') : 'pH';
  const kpiLabelEc = t('roomsPage.kpiEc') !== 'roomsPage.kpiEc' ? t('roomsPage.kpiEc') : 'EC';
  const kpiLabelLevel =
    t('roomsPage.kpiWaterLevel') !== 'roomsPage.kpiWaterLevel'
      ? t('roomsPage.kpiWaterLevel')
      : 'Water level';
  const overviewMetrics = useMemo(() => {
    const metrics = [];
    if (kpis.hasClimate && hasMetricValue(kpis.temp)) {
      metrics.push({
        key: 'temp',
        label: t('roomsPage.kpiTemperature'),
        value: `${Number(kpis.temp).toFixed(1)} °C`,
      });
    }
    if (kpis.hasClimate && hasMetricValue(kpis.rh)) {
      metrics.push({
        key: 'rh',
        label: t('roomsPage.kpiHumidity'),
        value: `${Number(kpis.rh).toFixed(0)} %`,
      });
    }
    if (kpis.hasClimate && hasMetricValue(kpis.co2)) {
      metrics.push({
        key: 'co2',
        label: t('roomsPage.kpiCo2'),
        value: `${Math.round(Number(kpis.co2))} ppm`,
      });
    }
    if (kpis.hasWater && hasMetricValue(kpis.ph)) {
      metrics.push({
        key: 'ph',
        label: kpiLabelPh,
        value: Number(kpis.ph).toFixed(2),
      });
    }
    if (kpis.hasWater && hasMetricValue(kpis.ec)) {
      metrics.push({
        key: 'ec',
        label: kpiLabelEc,
        value: Number(kpis.ec).toFixed(2),
      });
    }
    if (kpis.hasWater) {
      if (hasMetricValue(kpis.volume)) {
        metrics.push({
          key: 'volume',
          label: kpiLabelLevel,
          value: `${Math.round(Number(kpis.volume))} L`,
        });
      } else if (hasMetricValue(kpis.level)) {
        metrics.push({
          key: 'level',
          label: kpiLabelLevel,
          value: `${Math.round(Number(kpis.level))} cm`,
        });
      }
    }
    return metrics;
  }, [kpis, t, kpiLabelEc, kpiLabelLevel, kpiLabelPh]);

  const fetchDailySummary = useCallback(async () => {
    if (!token || !roomId || !room?.farmId || !hasDevices) return;
    setDailySummaryLoading(true);
    setDailySummaryErr('');
    try {
      const res = await authFetch(
        `/api/rooms/${encodeURIComponent(roomId)}/daily-summary?days=10&maxDocs=5000`,
        {},
        { token, onUnauthorized: handleUnauthorized, farmContextId: String(room.farmId) }
      );
      if (!res.ok) {
        setDailySummaryErr(t('roomsPage.analyticsError'));
        setDailySummary(null);
        return;
      }
      const data = await res.json();
      setDailySummary(data);
      const fromServer = {};
      for (const row of data.rows || []) {
        if (fromServer[row.date] === undefined) fromServer[row.date] = row.note ?? '';
      }
      setDayNotes(fromServer);
    } catch {
      setDailySummaryErr(t('roomsPage.analyticsError'));
      setDailySummary(null);
    } finally {
      setDailySummaryLoading(false);
    }
  }, [token, roomId, room?.farmId, hasDevices, handleUnauthorized, t]);

  useEffect(() => {
    fetchDailySummary();
  }, [fetchDailySummary]);

  const saveDayNote = useCallback(
    async (dayKey, text) => {
      if (!token || !room?.farmId) return;
      setSavingDayKey(dayKey);
      try {
        const res = await authFetch(
          `/api/rooms/${encodeURIComponent(roomId)}/daily-note`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ dayKey, note: text != null ? String(text) : '' }),
          },
          { token, onUnauthorized: handleUnauthorized, farmContextId: String(room.farmId) }
        );
        if (res.ok) await fetchDailySummary();
      } finally {
        setSavingDayKey(null);
      }
    },
    [token, room?.farmId, roomId, handleUnauthorized, fetchDailySummary]
  );

  const analyticsBlocks = useMemo(() => {
    const rows = dailySummary?.rows || [];
    const byDate = new Map();
    for (const r of rows) {
      if (!byDate.has(r.date)) byDate.set(r.date, {});
      byDate.get(r.date)[r.period] = r;
    }
    const dates = [...new Set(rows.map((r) => r.date))];
    return { byDate, dates };
  }, [dailySummary]);

  const resetWizard = () => {
    setWizardOpen(false);
    setWizardStep(1);
    setScannerEnabled(false);
    setManualDeviceId('');
    setClaimDeviceId('');
    setClaimError('');
    setClaimSuccess('');
    setClaimLoading(false);
  };

  const runClaim = async (incomingDeviceId) => {
    const normalizedId = extractDeviceId(incomingDeviceId);
    if (!normalizedId || !room?._id || !room?.farmId) {
      setClaimError(t('roomsPage.claimInvalidContext'));
      setWizardStep(2);
      return;
    }

    setClaimDeviceId(normalizedId);
    setClaimError('');
    setClaimSuccess('');
    setWizardStep(3);
    setClaimLoading(true);
    try {
      const res = await authFetch(
        '/api/devices/claim',
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            deviceId: normalizedId,
            farmId: String(room.farmId),
            roomId: String(room._id),
          }),
        },
        { token, onUnauthorized: handleUnauthorized, farmContextId: String(room.farmId) }
      );

      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        setClaimError(payload?.error || t('roomsPage.claimFailed'));
        return;
      }

      setClaimSuccess(t('roomsPage.claimSuccess'));
      setWizardStep(4);
      await load();
      setTimeout(() => {
        resetWizard();
      }, 1400);
    } catch (_) {
      setClaimError(t('roomsPage.claimFailed'));
    } finally {
      setClaimLoading(false);
    }
  };

  const addDiary = async (e) => {
    e.preventDefault();
    if (!token || !roomId) return;
    setSaving(true);
    try {
      const res = await authFetch(
        `/api/rooms/${encodeURIComponent(roomId)}/growing-logs`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note }),
        },
        {
          token,
          onUnauthorized: handleUnauthorized,
          farmContextId: room?.farmId ? String(room.farmId) : undefined,
        }
      );
      if (!res.ok) {
        setError(t('saveFailed'));
        return;
      }
      setNote('');
      const resLogs = await authFetch(
        `/api/rooms/${encodeURIComponent(roomId)}/growing-logs`,
        {},
        {
          token,
          onUnauthorized: handleUnauthorized,
          farmContextId: room?.farmId ? String(room.farmId) : undefined,
        }
      );
      const logData = resLogs.ok ? await resLogs.json() : [];
      setLogs(Array.isArray(logData) ? logData : []);
    } catch {
      setError(t('saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const saveDayNightCycle = useCallback(async () => {
    if (!token || !roomId || !room?.farmId) return;
    setCycleSaving(true);
    setCycleMessage('');
    try {
      const res = await authFetch(
        `/api/rooms/${encodeURIComponent(roomId)}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(cycleForm),
        },
        {
          token,
          onUnauthorized: handleUnauthorized,
          farmContextId: String(room.farmId),
        }
      );
      if (!res.ok) {
        setCycleMessage(t('roomsPage.dayNightCycleSaveFailed'));
        return;
      }
      await load({ silent: true });
      setCycleMessage(t('roomsPage.dayNightCycleSaved'));
    } catch (_) {
      setCycleMessage(t('roomsPage.dayNightCycleSaveFailed'));
    } finally {
      setCycleSaving(false);
    }
  }, [token, roomId, room?.farmId, cycleForm, handleUnauthorized, load, t]);

  const formatDate = useCallback(
    (str) => {
      if (!str) return '-';
      const locale = lang === 'he' ? 'he-IL' : lang === 'es' ? 'es-ES' : lang;
      return formatDeviceTimestamp(str, locale);
    },
    [lang]
  );

  const refreshDevice = useCallback(
    async (id) => {
      if (!token || !room) return;
      const current = cardDevices.find((d) => d.id === id || d.deviceId === id);
      const endpoint =
        current?.controller === 'water'
          ? `/api/sensor-data/water/latest/${encodeURIComponent(id)}`
          : `/api/sensor-data/climate/latest/${encodeURIComponent(id)}`;
      try {
        const res = await authFetch(endpoint, {}, { token, onUnauthorized: handleUnauthorized });
        if (!res.ok) return;
        if (current?.controller === 'water') {
          const payload = await res.json().catch(() => null);
          if (payload) {
            setRoom((prevRoom) =>
              mergeWaterPayloadIntoRoomDevices(prevRoom, payload, {
                source: 'manual-refresh',
                roomId,
              })
            );
          }
          return;
        }
        await load();
      } catch (err) {
        console.error('Failed to refresh device', err);
      }
    },
    [token, room, roomId, cardDevices, load, handleUnauthorized]
  );

  if (!ready) {
    return null;
  }

  if (!token) return <Navigate to="/login" replace />;

  const entries = [...logs].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  const validation = room?.dayNightValidation || {};
  const validationStatus = String(validation.status || 'UNKNOWN').toUpperCase();
  const validationReason = validation.reason || '';
  const actualLight = validation.actualLight || 'UNKNOWN';
  const expectedMode = validation.expectedMode || 'UNKNOWN';
  const cycleStatusText =
    validationReason === 'no_climate_controller'
      ? t('roomsPage.noClimateControllerAssigned')
      : validationReason === 'stale_telemetry' ||
          validationReason === 'missing_telemetry' ||
          validationReason === 'unknown_light_state'
        ? t('roomsPage.lightStatusUnknown')
        : validationStatus === 'MISMATCH'
          ? t('roomsPage.dayNightCycleMismatch')
          : validationStatus === 'OK'
            ? t('roomsPage.dayNightCycleOk')
            : t('roomsPage.dayNightCycleUnknown');
  return (
    <div className="container py-4 shefa-room-page" dir={dir}>
      <ShefaPageLoadOverlay show={Boolean(loading && !room)} />
      {!loading && room ? (
        <nav aria-label="breadcrumb" className="mb-2 shefa-room-breadcrumb-line">
          <ol className="breadcrumb mb-0">
            <li className="breadcrumb-item">
              <Link to={farmOverviewPath}>{t('nav.farm')}</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to={`/rooms/${encodeURIComponent(roomId)}`}>{room.name}</Link>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              {t('roomsPage.breadcrumbOverview')}
            </li>
          </ol>
        </nav>
      ) : null}
      {error && <div className="alert alert-danger">{error}</div>}
      {loading && !room ? null : !room ? null : (
        <>
          <div className="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-2">
            <div className="min-w-0">
              <h1 className="h3 mb-1">{room.name}</h1>
              {room.type || room.typeKey ? (
                <p className="text-muted mb-1">
                  {room.typeKey && t(`roomType.${room.typeKey}`) !== `roomType.${room.typeKey}`
                    ? t(`roomType.${room.typeKey}`)
                    : room.type}
                </p>
              ) : null}
            </div>
            <div className="d-flex flex-shrink-0 gap-1">
              <Link to={farmOverviewPath} className="btn btn-sm btn-outline-secondary">
                {t('nav.livePanel')}
              </Link>
              {canManageRoom ? (
                <>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => setRoomFormOpen(true)}
                  >
                    {t('roomsPage.editRoom')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={deleteRoom}
                    disabled={deleting}
                  >
                    {t('roomsPage.deleteRoom')}
                  </button>
                </>
              ) : null}
            </div>
          </div>
          {room.description ? <p className="mb-4">{room.description}</p> : null}

          <section className="card shadow-sm mb-4 shefa-room-day-cycle-card">
            <div className="card-body">
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                <h2 className="h6 text-uppercase text-muted mb-0">{t('roomsPage.dayNightCycleTitle')}</h2>
                <span className={`shefa-room-cycle-status shefa-room-cycle-status--${validationStatus.toLowerCase()}`}>
                  {cycleStatusText}
                </span>
              </div>
              <div className="row g-3 align-items-end">
                <div className="col-12 col-lg-4">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="room-day-cycle-enabled"
                      checked={cycleForm.dayCycleEnabled}
                      disabled={!canManageRoom}
                      onChange={(e) =>
                        setCycleForm((prev) => ({ ...prev, dayCycleEnabled: e.target.checked }))
                      }
                    />
                    <label className="form-check-label" htmlFor="room-day-cycle-enabled">
                      {t('roomsPage.dayNightCycleEnable')}
                    </label>
                  </div>
                </div>
                <div className="col-6 col-md-3 col-lg-2">
                  <label className="form-label small mb-1" htmlFor="room-day-cycle-start">
                    {t('roomsPage.dayStartTime')}
                  </label>
                  <input
                    id="room-day-cycle-start"
                    className="form-control form-control-sm"
                    type="time"
                    value={cycleForm.dayStartTime}
                    disabled={!canManageRoom}
                    onChange={(e) => setCycleForm((prev) => ({ ...prev, dayStartTime: e.target.value }))}
                  />
                </div>
                <div className="col-6 col-md-3 col-lg-2">
                  <label className="form-label small mb-1" htmlFor="room-day-cycle-end">
                    {t('roomsPage.dayEndTime')}
                  </label>
                  <input
                    id="room-day-cycle-end"
                    className="form-control form-control-sm"
                    type="time"
                    value={cycleForm.dayEndTime}
                    disabled={!canManageRoom}
                    onChange={(e) => setCycleForm((prev) => ({ ...prev, dayEndTime: e.target.value }))}
                  />
                </div>
                {canManageRoom ? (
                  <div className="col-12 col-md-3 col-lg-2">
                    <button
                      type="button"
                      className="btn btn-primary btn-sm w-100"
                      disabled={cycleSaving}
                      onClick={saveDayNightCycle}
                    >
                      {cycleSaving ? t('loading') : t('save')}
                    </button>
                  </div>
                ) : null}
              </div>
              <div className="shefa-room-cycle-grid mt-3">
                <div>
                  <span>{t('roomsPage.expectedMode')}</span>
                  <strong>{expectedMode}</strong>
                </div>
                <div>
                  <span>{t('roomsPage.actualLight')}</span>
                  <strong>{actualLight}</strong>
                </div>
                <div>
                  <span>{t('roomsPage.validationStatus')}</span>
                  <strong>{validationStatus}</strong>
                </div>
                <div>
                  <span>{t('roomsPage.lastCheckedTime')}</span>
                  <strong>{validation.lastCheckedAt ? formatDate(validation.lastCheckedAt) : '—'}</strong>
                </div>
              </div>
              {validationStatus === 'MISMATCH' ? (
                <div className="alert alert-warning py-2 px-3 mt-3 mb-0" role="alert">
                  {t('roomsPage.dayNightCycleMismatchWarning')}
                </div>
              ) : null}
              {cycleMessage ? <div className="small text-muted mt-2">{cycleMessage}</div> : null}
            </div>
          </section>

          {canManageRoom && room ? (
            <RoomFormModal
              open={roomFormOpen}
              onClose={() => setRoomFormOpen(false)}
              onSubmit={async (body) => {
                await saveRoomFromModal(body);
                setRoomFormOpen(false);
              }}
              initial={{
                name: room.name,
                typeKey: room.typeKey != null ? String(room.typeKey) : '',
                iconKey: room.iconKey != null ? String(room.iconKey) : 'leaf',
                description: room.description != null ? String(room.description) : '',
              }}
              editing
              farmLabel={farmName || String(room.farmId || '')}
              farmId={room.farmId ? String(room.farmId) : ''}
              t={t}
            />
          ) : null}

          {!hasDevices ? (
            <section className="card shadow-sm shefa-room-empty-state">
              <div className="card-body p-4 p-md-5 text-center">
                <div className="shefa-room-empty-state__icon mb-3">. . .</div>
                <h2 className="h4 mb-2">{t('roomsPage.notConnectedTitle')}</h2>
                <p className="text-muted mb-4">{t('roomsPage.notConnectedDescription')}</p>
                <button type="button" className="btn btn-success btn-lg" onClick={() => setWizardOpen(true)}>
                  {t('roomsPage.connectArgon')}
                </button>
              </div>
            </section>
          ) : (
            <>
              <section className="card shadow-sm mb-4 shefa-room-overview-kpis">
                <div className="card-body">
                  <h2 className="h6 text-uppercase text-muted mb-3">{t('roomsPage.breadcrumbOverview')}</h2>
                  <div className="row g-3 shefa-room-kpi-row w-100 m-0">
                    {overviewMetrics.map((metric) => (
                      <div key={metric.key} className="col-6 col-md-4 col-xl-3">
                        <div className="shefa-room-kpi">
                          <div className="shefa-room-kpi__label">{metric.label}</div>
                          <div className="shefa-room-kpi__value">{metric.value}</div>
                        </div>
                      </div>
                    ))}
                    <div className="col-6 col-md-4 col-xl-3">
                      <div className="shefa-room-kpi">
                        <div className="shefa-room-kpi__label">{t('roomsPage.kpiDevicesOnline')}</div>
                        <div className="shefa-room-kpi__value">
                          {kpis.online}/{kpis.total}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <section className="mb-4 shefa-room-climate-devices w-100 min-w-0">
                <h2 className="h5 mb-3">{t('roomsPage.devices')}</h2>
                <div className="row g-3 shefa-room-device-grid w-100 m-0">
                  {cardDevices.map((device) => {
                    const key = device.deviceId || device.id;
                    if (device.controller === 'water') {
                    return (
                      <div key={key} className="col-12 mb-0 shefa-device-card-slot">
                        <WaterStatusCard device={device} lang={lang} t={t} onRefresh={refreshDevice} roomId={roomId} />
                      </div>
                    );
                    }
                    return (
                      <div key={key} className="col-12 mb-0 shefa-device-card-slot">
                        <DeviceCard
                          device={device}
                          t={t}
                          lang={lang}
                          formatDate={formatDate}
                          onRefresh={refreshDevice}
                        />
                      </div>
                    );
                  })}
                </div>
              </section>

              <section className="card shadow-sm mb-4 shefa-room-analytics-card">
                <div className="card-body py-2 px-2">
                  <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                    <h2 className="h6 mb-0">{t('roomsPage.diaryAnalyticsTitle')}</h2>
                    {dailySummaryLoading ? (
                      <span className="spinner-border spinner-border-sm text-secondary" role="status" />
                    ) : null}
                    {dailySummary && !dailySummaryLoading ? (
                      <span className="small text-muted ms-auto">
                        {t('roomsPage.analyticsSamples')}: {dailySummary.samplesProcessed ?? 0}
                        {dailySummary.truncated ? ` · ${t('roomsPage.analyticsTruncated')}` : ''}
                      </span>
                    ) : null}
                  </div>
                  <p className="small text-muted mb-2">{t('roomsPage.growerDiarySummaryHint')}</p>
                  {dailySummary?.truncated && !dailySummaryLoading ? (
                    <div className="alert alert-warning py-2 px-2 small mb-2" role="status">
                      {t(
                        'roomsPage.analyticsTruncatedBanner',
                        dailySummary.aggregationSampleCap ?? 5000
                      )}
                    </div>
                  ) : null}
                  {dailySummaryErr ? (
                    <div className="alert alert-warning py-1 px-2 small mb-2">{dailySummaryErr}</div>
                  ) : null}
                  <div className="table-responsive">
                    <table className="table table-sm table-bordered align-middle mb-0 shefa-room-analytics-table">
                      <thead className="table-light">
                        <tr>
                          <th>{t('roomsPage.tableDate')}</th>
                          <th>{t('roomsPage.colPeriod')}</th>
                          <th>{t('roomsPage.colTempMm')}</th>
                          <th>{t('roomsPage.colRhMm')}</th>
                          <th>{t('roomsPage.colCo2Avg')}</th>
                          <th>{t('roomsPage.colVpdAvg')}</th>
                          <th>{t('roomsPage.colDailyNote')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analyticsBlocks.dates.length === 0 && !dailySummaryLoading ? (
                          <tr>
                            <td colSpan={7} className="text-muted text-center py-3 small">
                              {t('roomsPage.growerDiaryTablePlaceholder')}
                            </td>
                          </tr>
                        ) : null}
                        {analyticsBlocks.dates.map((date) => {
                          const dayRow = analyticsBlocks.byDate.get(date)?.day;
                          const nightRow = analyticsBlocks.byDate.get(date)?.night;
                          const thrD = dailySummary?.thresholds?.day;
                          const thrN = dailySummary?.thresholds?.night;
                          const renderMetricRow = (row, periodLabel, thr) => {
                            if (!row) {
                              return (
                                <tr key={`${date}-${periodLabel}`}>
                                  {periodLabel === t('roomsPage.periodDayShort') ? (
                                    <td rowSpan={2} className="text-nowrap small fw-semibold">
                                      {date}
                                    </td>
                                  ) : null}
                                  <td className="small">{periodLabel}</td>
                                  <td colSpan={4} className="text-muted small">
                                    —
                                  </td>
                                  {periodLabel === t('roomsPage.periodDayShort') ? (
                                    <td rowSpan={2} className="align-top p-1">
                                      <textarea
                                        className="form-control form-control-sm shefa-room-day-note"
                                        rows={2}
                                        value={dayNotes[date] ?? ''}
                                        onChange={(e) =>
                                          setDayNotes((prev) => ({ ...prev, [date]: e.target.value }))
                                        }
                                      />
                                      <button
                                        type="button"
                                        className="btn btn-outline-primary btn-sm mt-1 w-100 py-0"
                                        disabled={savingDayKey === date}
                                        onClick={() => saveDayNote(date, dayNotes[date])}
                                      >
                                        {savingDayKey === date ? t('loading') : t('roomsPage.dailyNoteSave')}
                                      </button>
                                    </td>
                                  ) : null}
                                </tr>
                              );
                            }
                            const tS = severityForRange(
                              row.temperature?.min,
                              row.temperature?.max,
                              thr?.temperature
                            );
                            const hS = severityForRange(row.humidity?.min, row.humidity?.max, thr?.humidity);
                            const cS = severityForValue(row.co2?.avg, thr?.co2);
                            const vS = severityForValue(row.vpd?.avg, null);
                            return (
                              <tr key={`${date}-${periodLabel}`}>
                                {periodLabel === t('roomsPage.periodDayShort') ? (
                                  <td rowSpan={2} className="text-nowrap small fw-semibold">
                                    {date}
                                  </td>
                                ) : null}
                                <td className="small">{periodLabel}</td>
                                <td className={`small text-nowrap ${sevClass(tS)}`}>
                                  {fmtMinMax(row.temperature?.min, row.temperature?.max, 1, '°')}
                                </td>
                                <td className={`small text-nowrap ${sevClass(hS)}`}>
                                  {fmtMinMax(row.humidity?.min, row.humidity?.max, 0, '%')}
                                </td>
                                <td className={`small text-nowrap ${sevClass(cS)}`}>
                                  {row.co2?.avg != null && Number.isFinite(row.co2.avg)
                                    ? `${Math.round(row.co2.avg)} ppm`
                                    : '—'}
                                </td>
                                <td className={`small text-nowrap ${sevClass(vS)}`}>
                                  {row.vpd?.avg != null && Number.isFinite(row.vpd.avg)
                                    ? `${Number(row.vpd.avg).toFixed(2)} kPa`
                                    : '—'}
                                </td>
                                {periodLabel === t('roomsPage.periodDayShort') ? (
                                  <td rowSpan={2} className="align-top p-1">
                                    <textarea
                                      className="form-control form-control-sm shefa-room-day-note"
                                      rows={2}
                                      value={dayNotes[date] ?? ''}
                                      onChange={(e) =>
                                        setDayNotes((prev) => ({ ...prev, [date]: e.target.value }))
                                      }
                                    />
                                    <button
                                      type="button"
                                      className="btn btn-outline-primary btn-sm mt-1 w-100 py-0"
                                      disabled={savingDayKey === date}
                                      onClick={() => saveDayNote(date, dayNotes[date])}
                                    >
                                      {savingDayKey === date ? t('loading') : t('roomsPage.dailyNoteSave')}
                                    </button>
                                  </td>
                                ) : null}
                              </tr>
                            );
                          };
                          return (
                            <React.Fragment key={date}>
                              {renderMetricRow(dayRow, t('roomsPage.periodDayShort'), thrD)}
                              {renderMetricRow(nightRow, t('roomsPage.periodNightShort'), thrN)}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </section>

              <section className="card shadow-sm mb-4">
                <div className="card-body">
                  <h2 className="h6 text-muted text-uppercase">{t('roomsPage.diaryTitle')}</h2>
                  <p className="small text-muted">{t('roomsPage.diaryRoomHint')}</p>
                  <form onSubmit={addDiary} className="mb-4">
                    <label className="form-label" htmlFor="room-diary-note">
                      {t('roomsPage.diaryNote')}
                    </label>
                    <textarea
                      id="room-diary-note"
                      className="form-control mb-2"
                      rows={3}
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                    />
                    <button type="submit" className="btn btn-primary" disabled={saving}>
                      {saving ? t('loading') : t('roomsPage.diaryAdd')}
                    </button>
                  </form>
                  {entries.length === 0 ? (
                    <p className="text-muted mb-0">{t('roomsPage.diaryEmpty')}</p>
                  ) : (
                    <ul className="list-unstyled">
                      {entries.map((e) => (
                        <li key={e._id} className="mb-4 pb-3 border-bottom">
                          <div className="small text-muted">
                            {e.timestamp ? new Date(e.timestamp).toLocaleString() : ''}
                            {e.authorName ? ` · ${e.authorName}` : ''}
                          </div>
                          {e.note ? <p className="mb-2 mt-1">{e.note}</p> : null}
                          <details className="small">
                            <summary>{t('roomsPage.diarySensors')}</summary>
                            {e.sensorReadings && Object.keys(e.sensorReadings).length ? (
                              <ul className="mt-2 mb-0">
                                {Object.entries(e.sensorReadings).map(([did, row]) => (
                                  <li key={did}>
                                    <span className="fw-semibold">{row.deviceLabel || did}</span>
                                    {row.offline ? (
                                      <span className="text-muted"> — {t('roomsPage.statusOffline')}</span>
                                    ) : (
                                      <span className="text-muted">
                                        {' '}
                                        — T {row.temperature ?? '—'}°C · RH {row.humidity ?? '—'}% · CO₂{' '}
                                        {row.co2 ?? '—'}
                                      </span>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <p className="mb-0 mt-1 text-muted">{t('roomsPage.sensorsNone')}</p>
                            )}
                          </details>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </section>
            </>
          )}
        </>
      )}
      {wizardOpen ? (
        <div className="modal fade show d-block" role="dialog" aria-modal="true">
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t('roomsPage.connectModalTitle')}</h5>
                <button type="button" className="btn-close" aria-label="Close" onClick={resetWizard} />
              </div>
              <div className="modal-body">
                {wizardStep === 1 ? (
                  <div>
                    <div className="shefa-room-wizard__step">{t('roomsPage.step1')}</div>
                    <h6 className="fw-semibold mb-2">{t('roomsPage.prepareDeviceTitle')}</h6>
                    <p className="mb-3">{t('roomsPage.prepareDeviceDescription')}</p>
                    <div className="shefa-room-wizard__pulse-hint">{t('roomsPage.searchingHint')}</div>
                  </div>
                ) : null}

                {wizardStep === 2 ? (
                  <div>
                    <div className="shefa-room-wizard__step">{t('roomsPage.step2')}</div>
                    <h6 className="fw-semibold mb-2">{t('roomsPage.identifyDeviceTitle')}</h6>
                    <p className="text-muted">{t('roomsPage.identifyDeviceDescription')}</p>
                    <div className="d-flex flex-wrap gap-2 mb-3">
                      <button
                        type="button"
                        className={`btn btn-outline-primary ${scannerEnabled ? 'active' : ''}`}
                        onClick={() => setScannerEnabled((prev) => !prev)}
                      >
                        {t('roomsPage.scanQr')}
                      </button>
                    </div>
                    {scannerEnabled ? (
                      <div className="shefa-room-wizard__scanner mb-3">
                        <QrReader
                          constraints={{ facingMode: 'environment' }}
                          onResult={(result) => {
                            const text = result?.getText?.() || result?.text || '';
                            const parsedId = extractDeviceId(text);
                            if (parsedId && !claimLoading) {
                              setScannerEnabled(false);
                              setManualDeviceId(parsedId);
                              runClaim(parsedId);
                            }
                          }}
                          containerStyle={{ width: '100%' }}
                        />
                      </div>
                    ) : null}
                    <label className="form-label" htmlFor="manual-device-id">
                      {t('roomsPage.deviceIdLabel')}
                    </label>
                    <div className="input-group">
                      <input
                        id="manual-device-id"
                        className="form-control"
                        value={manualDeviceId}
                        onChange={(e) => setManualDeviceId(e.target.value)}
                        placeholder={t('roomsPage.deviceIdPlaceholder')}
                      />
                      <button
                        type="button"
                        className="btn btn-primary"
                        disabled={!manualDeviceId.trim() || claimLoading}
                        onClick={() => runClaim(manualDeviceId)}
                      >
                        {t('roomsPage.connectDevice')}
                      </button>
                    </div>
                  </div>
                ) : null}

                {wizardStep === 3 ? (
                  <div className="text-center py-3">
                    <div className="shefa-room-wizard__step mb-2">{t('roomsPage.step3')}</div>
                    <div className="shefa-room-wizard__loader mx-auto mb-3" />
                    <h6 className="fw-semibold mb-1">{t('roomsPage.claimingInProgress')}</h6>
                    <p className="text-muted mb-0">ID: {claimDeviceId}</p>
                  </div>
                ) : null}

                {wizardStep === 4 ? (
                  <div className="text-center py-3">
                    <div className="display-6 mb-2">✨</div>
                    <h6 className="fw-semibold mb-2">{claimSuccess}</h6>
                    <p className="text-muted mb-0">{t('roomsPage.updatingRoomData')}</p>
                  </div>
                ) : null}

                {claimError ? <div className="alert alert-danger mt-3 mb-0">{claimError}</div> : null}
              </div>
              <div className="modal-footer">
                {wizardStep === 1 ? (
                  <button type="button" className="btn btn-primary" onClick={() => setWizardStep(2)}>
                    {t('roomsPage.next')}
                  </button>
                ) : null}
                {wizardStep === 2 ? (
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setWizardStep(1)}>
                    {t('roomsPage.back')}
                  </button>
                ) : null}
                {!claimLoading ? (
                  <button type="button" className="btn btn-outline-secondary" onClick={resetWizard}>
                    {t('roomsPage.close')}
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      ) : null}
      {wizardOpen ? <div className="modal-backdrop fade show" onClick={claimLoading ? undefined : resetWizard} /> : null}
    </div>
  );
}
